
import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { CompanyOfferSelection, CompanyOffersService } from '../../services/company-offers/company-offers.service';

@Component({
    selector: 'app-company-offer-selection',
    imports: [FormsModule, MatDialogModule, MatButtonModule, MatFormFieldModule, MatSelectModule],
    template: `<h2 mat-dialog-title>Company offers · {{data.name}}</h2>
  <mat-dialog-content>
    <p>Choose the products available in this company’s Business Offers page.</p>
    @if (loading) {
      <p role="status">Loading company offers…</p>
    }
    @if (error) {
      <p role="alert">{{error}}</p>
    }
    @if (selection) {
      <mat-form-field appearance="outline"><mat-label>Selection</mat-label>
      <mat-select [(ngModel)]="selection.mode" [disabled]="saving">
        <mat-option value="inherit">Inherit</mat-option><mat-option value="none">None</mat-option><mat-option value="custom">Custom</mat-option>
      </mat-select>
    </mat-form-field>
    @if (selection.mode === 'inherit') {
      <p>Use the nearest parent’s selection, or the current app brand’s campaigns when no company has a selection.</p>
    }
    @if (selection.mode === 'none') {
      <p>No company offers will be shown. This also overrides brand campaigns.</p>
    }
    @if (selection.mode === 'custom') {
      <p>This list replaces inherited selections and brand campaigns.</p>
      <mat-form-field appearance="outline"><mat-label>Company products</mat-label>
      <mat-select multiple [(ngModel)]="selection.productIds" [disabled]="saving">
        @for (candidate of selection.candidates; track candidate) {
          <mat-option [value]="candidate.id">{{candidate.name}} · {{candidate.vendorName}}</mat-option>
        }
        @for (id of unavailableIds; track id) {
          <mat-option [value]="id">Unavailable product · {{id}} — remove to save</mat-option>
        }
      </mat-select>
    </mat-form-field>
    @if (!selection.candidates.length) {
      <p>No compatible products yet. Enable Companies on an offer with a literal HTTPS linkout and map it in the vendor product’s reward metadata.</p>
    }
  }
  <p>Employee products and payment settings are managed separately.</p>
  }
  </mat-dialog-content>
  <mat-dialog-actions align="end"><button mat-button mat-dialog-close [disabled]="saving">Cancel</button>
  @if (!selection && !loading) {
    <button mat-stroked-button (click)="load()">Try again</button>
  }
  <button mat-raised-button color="primary" [disabled]="!selection || loading || saving || (selection.mode === 'custom' && (!selection.productIds.length || unavailableIds.length > 0))" (click)="save()">{{saving ? 'Saving…' : 'Save'}}</button>
  </mat-dialog-actions>`,
    changeDetection: ChangeDetectionStrategy.Eager,
    styles: [`mat-form-field { display: block; width: 100%; }`]
})
export class CompanyOfferSelectionComponent implements OnInit {
  selection?: CompanyOfferSelection;
  loading = true;
  saving = false;
  error = '';
  constructor(@Inject(MAT_DIALOG_DATA) public data: { id: string; name: string }, private api: CompanyOffersService,
    private dialog: MatDialogRef<CompanyOfferSelectionComponent>) {}
  get unavailableIds(): string[] {
    return this.selection?.productIds.filter(id => !this.selection?.candidates.some(candidate => candidate.id === id)) || [];
  }
  ngOnInit(): void { void this.load(); }
  async load(): Promise<void> {
    this.loading = true; this.error = '';
    try { this.selection = await this.api.getSelection(this.data.id); }
    catch (_) { this.error = 'Unable to load company offer settings.'; }
    finally { this.loading = false; }
  }
  async save(): Promise<void> {
    if (!this.selection || this.saving) return;
    this.saving = true; this.error = '';
    try {
      const result = await this.api.saveSelection(this.data.id, { mode: this.selection.mode, productIds: this.selection.productIds });
      this.dialog.close(result);
    } catch (error: any) { this.error = error?.data?.error || error?.error?.error || 'Unable to save company offers.'; }
    finally { this.saving = false; }
  }
}
